import { useState } from 'react';
import { DATA_BASE } from '../services/apiClient';

/**
 * Foto de una persona (alumno o cajero) con caída a INICIALES sobre un color estable.
 *
 * Nació dentro de la Caja (rediseño sep-2026: Kydemy pone la cara del alumno y la del cajero en
 * cada renglón) y se sacó aquí al reutilizarlo en Ingresos. El color no es aleatorio: se deriva
 * del nombre, así que la misma persona sale siempre del mismo color y se reconoce de un vistazo.
 *
 * 🪤 18-sep-2026 — POR QUÉ NO USA `/api/avatars/<kind>/<id>` (el del núcleo):
 * ese endpoint solo mira el almacén de avatares SUBIDOS de Lealify, y en Let's Move nadie ha
 * subido ninguno → 404 → iniciales para todo el mundo. Pero las caras SÍ existen: las 805 fotos
 * que se bajaron de Kydemy viven en `letsmove_member_profile.photo_local` (790 de 791 alumnos) y
 * en `letsmove_staff_profile.photo_local` (21 de 22 del staff, las 3 cajeras incluidas), y ya hay
 * endpoints propios que las sirven. Este componente pide ESOS, que son los mismos que usan la
 * lista de Alumnos y los reportes de bajas/inactivos.
 * Si un día alguien sube su propia foto por la ficha, el POST reescribe `photo_local` → esta
 * misma URL devuelve la nueva. Una sola fuente, sin copiar archivos a dos sitios.
 */
const TONOS = [
  'bg-sky-500/20 text-sky-700 dark:text-sky-300',
  'bg-emerald-500/20 text-emerald-700 dark:text-emerald-300',
  'bg-violet-500/20 text-violet-700 dark:text-violet-300',
  'bg-amber-500/20 text-amber-700 dark:text-amber-300',
  'bg-rose-500/20 text-rose-700 dark:text-rose-300',
];

export default function Avatar({ kind, id, name, size = 22 }: {
  kind: 'user' | 'contact';
  id?: number | null;
  name?: string | null;
  size?: number;
}) {
  const [roto, setRoto] = useState(false);
  const ini = (name || '?').trim().split(/\s+/).slice(0, 2).map((w) => w[0]).join('').toUpperCase() || '?';
  const tono = TONOS[[...(name || '?')].reduce((s, c) => s + c.charCodeAt(0), 0) % TONOS.length];
  const style = { width: size, height: size, fontSize: Math.round(size * 0.4) };

  if (!id || roto) {
    return (
      <span style={style} className={`inline-flex shrink-0 items-center justify-center rounded-full font-semibold ${tono}`}
        title={name || undefined}>
        {ini}
      </span>
    );
  }
  // contact → alumno (member_profile) · user → cajero/profe (staff_profile). Son rutas distintas
  // porque el staff no está en member_profile: pedir ahí la foto de una cajera daba 404 siempre.
  const src = kind === 'contact' ? `${DATA_BASE}/members/${id}/photo` : `${DATA_BASE}/teachers/${id}/photo`;
  return (
    <img src={src} alt="" title={name || undefined} style={style} loading="lazy"
      onError={() => setRoto(true)}
      className="inline-block shrink-0 rounded-full object-cover" />
  );
}
